/* eslint-disable @next/next/no-img-element */
import { useEffect, useState, useRef } from "react";
import { useRecoilValue } from "recoil";
import clsx from "clsx";
import { motion } from "framer-motion";

import styles from "../../styles/tinymce.module.css";

import { engageState } from "../../atoms/platform";
import { getItemNumberOnly, getGroupForTitleId } from "../../helpers/text";
import { textToHtmlTinyMCE } from "../../helpers/converter";
import { getNameByGroupId } from "../../helpers/arrays";
import { apiGetTopicPrintClean } from "../../lib/api/topics";
import { PRINT_HOST, ENV_MODE } from "../../helpers/printing";

import { XIcon } from "@heroicons/react/outline";
import { AiOutlinePrinter } from "react-icons/ai";

import EditorReadOnly from "../RichText/EditorReadOnly";
import CustomFieldPrint from "../Actions/Dark/CustomFieldPrint";
import SearchVideoV2 from "./SearchVideoV2";
import Loading from "../Shared/Loading";
import AgendaBillEngage from "./AgendaBillEngage";
import AdobeViewer from "../Pdf/AdobeViewer";
import EditorHTML from "../RichText/EditorHTML";
import TallyResults from "./TallyResults";

const AgendaTopicFull = ({ visible, topic, enablePri, client }) => {
  const platform = useRecoilValue(engageState);
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const [ranked, setRanked] = useState();
  const [resources, setResources] = useState([]);
  const [mobileFile, setMobileFile] = useState();
  const [localTopic, setLocalTopic] = useState();
  const [loading, setLoading] = useState(false);
  const [toc, setToc] = useState([]);
  const [activeToc, setActiveToc] = useState();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        visible(false);
      }
    };

    if (visible) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [visible]);

  useEffect(() => {
    // Store the element that triggered the modal
    previousFocusRef.current = document.activeElement;

    // Move focus into the modal once it renders
    const timer = setTimeout(() => {
      const closeButton = modalRef.current?.querySelector("button");
      if (closeButton) closeButton.focus();
    }, 100); // small delay to let framer-motion animate in

    return () => {
      clearTimeout(timer);
      // Return focus to the trigger when modal closes
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    function handleTab(e) {
      if (e.key !== "Tab") return;

      const focusable = modal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: if on first element, wrap to last
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab: if on last element, wrap to first
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    window.addEventListener("keydown", handleTab);
    return () => window.removeEventListener("keydown", handleTab);
  }, [localTopic]);

  useEffect(() => {
    async function fetchTopic() {
      setLoading(true);
      const result = await apiGetTopicPrintClean(topic);
      if (result) {
        setLocalTopic(result);
        let minutesContent;
        try {
          minutesContent = JSON.parse(result.minutesInfo)?.trim();
        } catch {
          minutesContent = result.minutesInfo?.trim();
        }
        const hasValidMinutes =
          minutesContent && !/^[\/\s]+$/.test(minutesContent);
        if (result.attachments?.length > 0) {
          const filteredArray = result.attachments.filter(
            (obj) =>
              obj.public === true && obj.fileType?.toLowerCase() === "pdf",
          );
          setResources(filteredArray);
          setMobileFile(filteredArray[0]);
          let tempToc = [];
          if (client === "federalwaywa") {
            tempToc.push({
              id: "bill",
              fileType: "bill",
              fileName: "Agenda Bill",
            });
          }
          if (result.hasDetails) {
            tempToc.push({
              id: "report",
              fileType: "report",
              fileName: "Report",
            });
          }
          if (result.meeting?.publishStatus === "minutes") {
            if (hasValidMinutes) {
              tempToc.push({
                id: "minutes",
                fileType: "minutes",
                fileName: "Minutes",
              });
            }
          }
          tempToc.push(...filteredArray);
          if (tempToc.length > 0) {
            setActiveToc(tempToc[0]);
          }
          setToc(tempToc);
        } else {
          let tempToc = [];
          if (client === "federalwaywa") {
            tempToc.push({
              id: "bill",
              fileType: "bill",
              fileName: "Agenda Bill",
            });
          }
          if (result.hasDetails) {
            tempToc.push({
              id: "report",
              fileType: "report",
              fileName: "Report",
            });
          }
          if (result.meeting?.publishStatus === "minutes") {
            if (hasValidMinutes) {
              tempToc.push({
                id: "minutes",
                fileType: "minutes",
                fileName: "Minutes",
              });
            }
          }
          if (tempToc.length > 0) {
            setActiveToc(tempToc[0]);
          }
          setToc(tempToc);
        }
        if (result.rankedVoting) {
          setRanked(JSON.parse(result.rankedVoting));
        }
      }
      setLoading(false);
    }
    if (topic) {
      // if (client === "federalwaywa") {
      //   setSection(3);
      // }
      fetchTopic();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic]);

  // const handleFileChange = (value) => {
  //   setMobileFile(value);
  // };

  return (
    <motion.div
      key="slide-over"
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-label={localTopic ? localTopic.title : "Agenda topic details"}
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "tween", duration: 0.5 }}
      className="fixed inset-0 z-50 flex bg-white"
    >
      <motion.div
        initial={{ x: 100 }}
        animate={{ x: 0 }}
        exit={{ x: 100 }}
        transition={{ duration: 0.5 }}
        className="relative w-full h-full bg-white overflow-hidden"
      >
        {localTopic && (
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="px-4 border-b border-zinc-700 pb-1">
              <div className="mt-4 flex justify-between items-end">
                <div className="w-1/2">
                  <p className="font-semibold text-sm 2xl:text-base truncate">
                    {localTopic.title}
                  </p>
                </div>
                <div
                  className={clsx(
                    "flex items-end justify-end space-x-3 pb-0.5",
                  )}
                >
                  <div className="space-y-1">
                    {localTopic?.hasDetails && (
                      <div className="2xl:text-lg flex font-medium text-black">
                        {localTopic?.editorType === 1 ? (
                          <a
                            href={`${process.env.NEXT_PUBLIC_BASE_URL}/${client}/print/action/${topic}?print=true`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center hover:text-blue-500 transition duration-200"
                            aria-label="Print agenda topic"
                          >
                            <AiOutlinePrinter
                              className="h-6 w-6 mr-4"
                              aria-hidden="true"
                            />
                          </a>
                        ) : (
                          <a
                            href={`${PRINT_HOST}/v6/${client}/${topic}?mode=${ENV_MODE}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center hover:text-blue-500 transition duration-200"
                            aria-label="Print agenda topic"
                          >
                            <AiOutlinePrinter
                              className="h-6 w-6 mr-4"
                              aria-hidden="true"
                            />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="h-7 flex items-end">
                    <button
                      type="button"
                      className="rounded-md text-black hover:text-delete-btn focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer hover:rotate-180 transform duration-150"
                      onClick={() => {
                        visible(false);
                      }}
                    >
                      <span className="sr-only">Close panel</span>
                      <XIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="h-full flex flex-nowrap overflow-hidden">
              {/* Menu Selection */}
              <nav
                aria-label="Table of Contents"
                className="w-1/6 2xl:w-1/4 border-r border-black flex flex-col overflow-hidden"
              >
                <p
                  id="toc-heading"
                  className="text-center pt-1.5 pb-2 font-semibold text-sm 2xl:text-base flex-shrink-0"
                >
                  Table of Contents
                </p>
                <ul
                  aria-labelledby="toc-heading"
                  className="overflow-y-auto pb-2"
                >
                  {toc.map((file, index) => (
                    <li
                      key={file.id || file._id || index}
                      className="mb-2 overflow-hidden w-full"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setActiveToc(file);
                        }} // your action here
                        className={clsx(
                          file.id.toString() === activeToc?.id
                            ? "bg-blue-400 text-white"
                            : "bg-gray-100 text-black",
                          "w-full text-left px-2 py-1.5 rounded",
                          "hover:bg-gray-200 hover:text-black focus-visible:ring-2 focus-visible:ring-blue-500",
                          "transition font-medium text-sm 2xl:text-base",
                          // ↓ ensure proper truncation behavior
                          "block overflow-hidden text-ellipsis break-words",
                          "line-clamp-2 2xl:line-clamp-1",
                        )}
                      >
                        {file.fileName}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
              {/* Details */}
              <div className="w-5/6 2xl:w-3/4 h-screen flex flex-col overflow-hidden">
                {/* Agenda Bill */}
                {activeToc?.fileType === "bill" && (
                  <div className="mx-4 2xl:mx-6 overflow-hidden">
                    <AgendaBillEngage topic={localTopic} client={client} />
                  </div>
                )}
                {/* Document */}

                {activeToc?.fileType === "report" && platform && localTopic && (
                  <>
                    {localTopic.editorType === 1 ? (
                      <div className="grid grid-cols-6 mx-4 2xl:mx-6 overflow-y-auto pb-md">
                        <div className="relative col-span-2 overflow-hidden min-h-[7em]">
                          <img
                            className="absolute object-contain h-full w-full p-4"
                            src={platform.logo}
                            alt={`${platform.clientName} logo`}
                          />
                        </div>
                        <div className="col-span-4 border-b border-black mt-2">
                          <div className="h-full w-full overflow-hidden grid grid-rows-2">
                            <div className="flex justify-end items-end font-semibold text-xl">
                              <h3 className="">{platform.clientName}</h3>
                            </div>
                            <div className="flex justify-end items-center font-semibold text-lg">
                              <h3 className="text-right">
                                {localTopic.department?.name}{" "}
                                {platform.memoCalled}
                              </h3>
                            </div>
                          </div>
                        </div>
                        {platform?.fieldPreparedBy && (
                          <div className="col-span-6 w-full flex justify-end">
                            <p className="text-[12px]">
                              Prepared By: {localTopic.modifiedBy?.name}
                            </p>
                          </div>
                        )}
                        {/* Agenda Title */}
                        <div className="mt-4 col-span-6 border-b border-gray-800 text-[12px]">
                          <p className="font-semibold 2xl:text-lg">
                            {localTopic.title}
                          </p>
                          <p className="whitespace-pre-line">
                            {localTopic.topicBlurb}
                          </p>
                          {Array.isArray(localTopic.subTopics) &&
                            localTopic.subTopics.length > 0 &&
                            localTopic.subTopics.map((sub, sIdx) => {
                              return (
                                <p key={`sub-${sIdx}`} className="ml-8 py-0.5">
                                  &#8212; {sub}
                                </p>
                              );
                            })}
                        </div>
                        {/* General Info */}
                        <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                          <div className="bg-gray-200 border-b border-gray-800 mb-1">
                            <p className="text-center font-medium">Meeting</p>
                          </div>
                          <div className="">
                            <p className="text-center">
                              {localTopic.meeting?.scheduleDate}
                            </p>
                          </div>
                        </div>
                        <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                          <div className="bg-gray-200 border-b border-gray-800 mb-1">
                            <p className="text-center font-medium">
                              Agenda Group
                            </p>
                          </div>
                          <div className="flex items-center justify-evenly">
                            <p className="">
                              {getNameByGroupId(
                                localTopic.agendaGroup.id,
                                localTopic.meeting?.groupSubheadings,
                                localTopic.agendaGroup.name,
                              )}
                            </p>
                            <p className="">
                              Item:{" "}
                              {localTopic.meeting && (
                                <>
                                  {getGroupForTitleId(
                                    localTopic.meeting.groupNumbering,
                                    localTopic.agendaGroup.id,
                                    localTopic.meeting.groupOrder,
                                    localTopic.meeting.childNumbering,
                                  )}
                                  {getItemNumberOnly(
                                    localTopic.meeting.allTopics,
                                    localTopic.meeting.itemNumbering,
                                    localTopic,
                                    localTopic.order - 1,
                                    localTopic.meeting.groupNumbering,
                                  )}
                                </>
                              )}
                            </p>
                          </div>
                        </div>
                        {localTopic.votedByBoard && (
                          <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                            <div className="bg-gray-200 border-b border-gray-800 mb-1">
                              <p className="text-center font-medium">
                                Voted By Board
                              </p>
                            </div>
                            <div className="flex items-center justify-evenly">
                              <p className="">
                                {localTopic.votedByBoard?.name}
                              </p>
                            </div>
                          </div>
                        )}
                        {/* File and Presenter */}
                        {platform?.fieldReferenceFile && (
                          <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                            <div className="bg-gray-200 border-b border-gray-800 mb-1">
                              <p className="text-center font-medium">
                                Reference File
                              </p>
                            </div>
                            <div>
                              <div className="min-h-[12px]">
                                <p className="text-center">
                                  {localTopic.referenceFile}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        {platform?.fieldPresentedBy && (
                          <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                            <div className="bg-gray-200 border-b border-gray-800 mb-1">
                              <p className="text-center font-medium">
                                Presented By
                              </p>
                            </div>
                            <div className="">
                              <p className="text-center">
                                {localTopic.presentedBy}
                              </p>
                            </div>
                          </div>
                        )}
                        {/* Custom Fields */}
                        <CustomFieldPrint
                          client={client}
                          fields={localTopic.customFields}
                        />
                        {/* Community Goals */}
                        {platform?.enablePriorities && (
                          <div className="mt-2 col-span-6 border border-gray-800 text-[12px]">
                            <div className="bg-gray-200 border-b border-gray-800 mb-1">
                              <p className="text-center font-medium">
                                Community Goals
                              </p>
                            </div>
                            <div className="px-3 py-2">
                              <ul className="flex flex-wrap space-x-2 list-none m-0 p-0">
                                {localTopic.priorities.map((item, index) => {
                                  if (item.checked) {
                                    return (
                                      <li
                                        key={index}
                                        className="flex mt-0.5 items-center pl-2"
                                      >
                                        <p className="font-medium">
                                          &#9745; {item.name}
                                        </p>
                                      </li>
                                    );
                                  } else return null;
                                })}
                              </ul>
                            </div>
                          </div>
                        )}
                        {/* Details */}
                        <div className="py-1 col-span-6">
                          <EditorReadOnly
                            value={localTopic.details}
                            docId={localTopic.id}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="overflow-y-auto pb-sm">
                        {localTopic.useBuiltInHeader && (
                          <div className="grid grid-cols-6 px-4 bg-white rounded-t-md pb-6 2xl:max-w-7xl 2xl:mx-auto">
                            <div className="relative col-span-2 overflow-hidden min-h-[7em]">
                              <img
                                className="absolute object-contain h-full w-full p-4"
                                src={platform.logo}
                                alt=""
                              />
                            </div>
                            <div className="col-span-4 border-b border-black mt-2">
                              <div className="h-full w-full overflow-hidden grid grid-rows-2">
                                <div className="flex justify-end items-end font-semibold text-xl">
                                  <h3 className="">{platform.clientName}</h3>
                                </div>
                                <div className="flex justify-end items-center font-semibold text-lg">
                                  <h3 className="text-right">
                                    {localTopic.department?.name}{" "}
                                    {platform.memoCalled}
                                  </h3>
                                </div>
                              </div>
                            </div>
                            {/* Agenda Title */}
                            <div className="mt-4 col-span-6 border-b border-gray-800 text-[12px]">
                              <p className="font-semibold 2xl:text-lg">
                                {localTopic.title}
                              </p>
                              <p className="whitespace-pre-line">
                                {localTopic.topicBlurb}
                              </p>
                              {localTopic.subTopics?.length > 0 &&
                                localTopic.subTopics.map((sub, sIdx) => {
                                  return (
                                    <p
                                      key={`sub-${sIdx}`}
                                      className="ml-8 py-0.5"
                                    >
                                      &#8212; {sub}
                                    </p>
                                  );
                                })}
                            </div>
                            {/* General Info */}
                            <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                              <div className="bg-gray-200 border-b border-gray-800 mb-1">
                                <p className="text-center font-medium">
                                  Meeting
                                </p>
                              </div>
                              <div className="">
                                <p className="text-center">
                                  {localTopic.meeting?.scheduleDate}
                                </p>
                              </div>
                            </div>
                            <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                              <div className="bg-gray-200 border-b border-gray-800 mb-1">
                                <p className="text-center font-medium">
                                  Agenda Group
                                </p>
                              </div>
                              <div className="flex items-center justify-evenly">
                                {localTopic.meeting ? (
                                  <>
                                    <p className="">
                                      {getNameByGroupId(
                                        localTopic.agendaGroup.id,
                                        localTopic.meeting.groupSubheadings,
                                        localTopic.agendaGroup.name,
                                      )}
                                    </p>
                                    <p className="">
                                      Item:{" "}
                                      {getGroupForTitleId(
                                        localTopic.meeting.groupNumbering,
                                        localTopic.agendaGroup.id,
                                        localTopic.meeting.groupOrder,
                                        localTopic.meeting.childNumbering,
                                      )}
                                      {getItemNumberOnly(
                                        localTopic.meeting.allTopics,
                                        localTopic.meeting.itemNumbering,
                                        localTopic,
                                        localTopic.order - 1,
                                        localTopic.meeting.groupNumbering,
                                      )}
                                    </p>
                                  </>
                                ) : (
                                  <>
                                    <p className="">
                                      {localTopic.agendaGroup?.parentGroup?.name
                                        ? `${localTopic.agendaGroup.parentGroup.name} - ${localTopic.agendaGroup.name}`
                                        : localTopic.agendaGroup?.name}
                                    </p>
                                    <p className="">Item: N/A</p>
                                  </>
                                )}
                              </div>
                            </div>
                            {/* General Info */}
                            {localTopic.votedByBoard && (
                              <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                                <div className="bg-gray-200 border-b border-gray-800 mb-1">
                                  <p className="text-center font-medium">
                                    Voted By Board
                                  </p>
                                </div>
                                <div className="flex items-center justify-evenly">
                                  <p className="">
                                    {localTopic.votedByBoard?.name}
                                  </p>
                                </div>
                              </div>
                            )}
                            {/* File and Presenter */}
                            {platform?.fieldReferenceFile && (
                              <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                                <div className="bg-gray-200 border-b border-gray-800 mb-1">
                                  <p className="text-center font-medium">
                                    Reference File
                                  </p>
                                </div>
                                <div>
                                  <div className="min-h-[12px]">
                                    <p className="text-center">
                                      {localTopic.referenceFile}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                            {platform?.fieldPresentedBy && (
                              <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                                <div className="bg-gray-200 border-b border-gray-800 mb-1">
                                  <p className="text-center font-medium">
                                    Presented By
                                  </p>
                                </div>
                                <div className="">
                                  <p className="text-center">
                                    {localTopic.presentedBy}
                                  </p>
                                </div>
                              </div>
                            )}
                            {/* Custom Fields */}
                            <CustomFieldPrint
                              client={localTopic.clientId}
                              fields={localTopic.customFields}
                            />
                            {/* Community Goals */}
                            {platform?.enablePriorities && (
                              <div className="mt-2 col-span-6 border border-gray-800 text-[12px]">
                                <div className="bg-gray-200 border-b border-gray-800 mb-1">
                                  <p className="text-center font-medium">
                                    Community Goals
                                  </p>
                                </div>
                                <div className="px-3 py-2">
                                  <div className="flex flex-wrap space-x-2">
                                    {localTopic.priorities.map(
                                      (item, index) => {
                                        if (item.checked) {
                                          return (
                                            <li
                                              key={index}
                                              className="flex mt-0.5 items-center pl-2"
                                            >
                                              <p className="font-medium">
                                                &#9745; {item.name}
                                              </p>
                                            </li>
                                          );
                                        } else return null;
                                      },
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        <div
                          className={`relative mb-4 px-4 2xl:px-10 mt-2 2xl:mt-6 ${styles.tinyContainer}`}
                        >
                          {textToHtmlTinyMCE(localTopic.details)}
                        </div>
                      </div>
                    )}
                  </>
                )}
                {activeToc?.fileType === "minutes" && localTopic && (
                  <div className="mt-4 overflow-y-auto pb-sm">
                    {localTopic?.minutesInfo?.length > 0 && (
                      <>
                        {localTopic.minutesEditor === 1 && (
                          <EditorHTML value={localTopic.minutesInfo} />
                        )}
                        {localTopic.minutesEditor === 2 && (
                          <div
                            className={`mb-4 px-4 2xl:px-10 mt-2 2xl:mt-6 ${styles.tinyContainer}`}
                          >
                            {textToHtmlTinyMCE(localTopic.minutesInfo)}
                          </div>
                        )}
                      </>
                    )}
                    {/* Motions */}
                    {localTopic.motions?.length > 0 &&
                      localTopic.hasDetails && (
                        <div className="sm:space-y-2 flex flex-col mb-4 mt-4 px-4">
                          <p className="font-semibold">Motions</p>
                          <div>
                            {localTopic.motions.map((motion, index) => (
                              <div
                                className="flex flex-col space-y-1"
                                key={index}
                              >
                                <p className="font-medium text-base">
                                  {index + 1}. Motion Type:{" "}
                                  {motion.motionType?.name}
                                </p>
                                <p className="pl-4 font-medium">
                                  Motion By: {motion.motionedBy.name}
                                </p>
                                <p className="pl-4 font-medium">
                                  Seconded By: {motion.secondedBy?.name}
                                </p>
                                {motion.wording?.length > 0 && (
                                  <p className="pl-4 leading-none italic border-t border-zinc-700 pt-1">
                                    {motion.wording}
                                  </p>
                                )}
                                <p className="pl-4">
                                  Vote Result:{" "}
                                  {motion.votingResult === true
                                    ? "Passed"
                                    : "Failed"}
                                </p>
                                {localTopic.votingOption !== "RANKED" ? (
                                  <>
                                    {/* <p className="pl-4">Votes:</p> */}
                                    <div className="grid grid-cols-1 lg:grid-cols-3 2xl:grid-cols-4 gap-1">
                                      {!motion.secretBallot &&
                                        motion.votes.map((vote, index) => {
                                          if (vote.counts !== "recuse") {
                                            return (
                                              <div
                                                key={vote._id}
                                                className={clsx(
                                                  vote.counts === "positive"
                                                    ? "bg-green-700"
                                                    : "",
                                                  vote.counts === "negative"
                                                    ? "bg-red-700"
                                                    : "",
                                                  vote.counts === "no-vote"
                                                    ? "bg-zinc-700"
                                                    : "",
                                                  vote.counts === "recuse"
                                                    ? "bg-zinc-700"
                                                    : "",
                                                  vote.counts === "absent"
                                                    ? "bg-zinc-700"
                                                    : "",
                                                  "relative rounded-lg pr-2 flex items-center space-x-2",
                                                )}
                                              >
                                                <div className="flex-shrink-0">
                                                  <img
                                                    className="h-10 w-10 rounded-l-md"
                                                    src={vote.member.photoUrl}
                                                    alt=""
                                                  />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="focus:outline-none">
                                                    <span className="absolute inset-0" />
                                                    <p className="text-xs lg:text-sm 2xl:text-base text-zinc-200">
                                                      {vote.member.name}
                                                    </p>
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          } else {
                                            return null;
                                          }
                                        })}
                                    </div>

                                    {motion.secretBallot && (
                                      <TallyResults votes={motion.votes} />
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <p className="pl-4">
                                      Ranked Vote with {ranked?.openings} spots
                                      filled:
                                    </p>
                                    {ranked?.approved.map((vote, index) => (
                                      <p
                                        className="pl-8 font-medium py-0"
                                        key={index * 300}
                                      >
                                        {vote}
                                      </p>
                                    ))}
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                )}
                {/* Attachments */}
                {activeToc?.fileType === "pdf" &&
                  !activeToc?.docAccess?.enabled && (
                    <div className="flex-1 flex flex-col overflow-hidden px-2 min-h-0 pb-32">
                      <div className="flex-1 overflow-hidden">
                        <div className="h-full w-full">
                          <AdobeViewer
                            url={activeToc?.fileUrl}
                            fileName={activeToc?.fileName}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                {activeToc?.fileType === "pdf" &&
                  activeToc?.docAccess?.enabled &&
                  activeToc?.docAccess?.accessUrl && (
                    <div className="flex-1 flex flex-col overflow-hidden px-2 min-h-0 pb-32">
                      <div className="flex-1 overflow-hidden flex flex-col">
                        <div className="flex justify-start px-2 py-1 flex-shrink-0">
                          <a
                            href={activeToc.docAccess.accessUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm 2xl:text-base text-blue-600 hover:text-blue-800 underline"
                          >
                            Open in new tab
                          </a>
                        </div>
                        <iframe
                          src={activeToc.docAccess?.accessUrl}
                          className="h-full w-full border-0"
                          title="Document Viewer"
                          allow="fullscreen"
                        />
                      </div>
                    </div>
                  )}
              </div>
              {loading && <Loading />}
              {typeof localTopic?.elapsedSec === "number" &&
                localTopic?.meeting?.videoUrl && (
                  <SearchVideoV2
                    elapsedSeconds={localTopic.elapsedSec}
                    videoUrl={localTopic.meeting.videoUrl}
                  />
                )}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default AgendaTopicFull;
